/** biome-ignore-all lint/performance/noImgElement: disabled */
import { Button, Spinner } from "@components";
import { Camera01, ChevronLeft, Edit05, Trash01 } from "@untitledui/icons";
import { type Dispatch, type SetStateAction, useState } from "react";
import { cx } from "@/utils/cx";
import type { FileUploadStatusValue } from "../types";
import { FileUploadStatus } from "../types";

type ExpandedImageProps = {
	onClose: Dispatch<SetStateAction<boolean>>;
	previewUri: string;
};

type CameraPreviewProps = {
	previewUri?: string | null;
	onClick: () => void;
	label: string;
	icon: "camera" | "edit";
	className?: string;
	objectPosition?: "object-cover" | "object-center" | "object-contain";
	error?: boolean;
	isRequired?: boolean;
	onRemove?: () => void;
	uploadStatus?: FileUploadStatusValue;
	uploadErrorMessage?: string;
	onRetry?: () => void;
};

const ExpandedImage = ({ onClose, previewUri }: ExpandedImageProps) => (
	<div className="fixed inset-0 z-10 h-dvh w-dvw bg-gray-900">
		<button
			className="absolute top-5 left-4 z-20 rounded-full bg-black/50 p-2"
			onClick={() => onClose(false)}
			type="button"
		>
			<ChevronLeft className="text-gray-100" size={16} />
		</button>
		<img
			alt="Imagem de preview"
			className={cx("mx-auto h-full w-auto object-contain")}
			height={96}
			src={previewUri}
			width={100}
		/>
	</div>
);

export function ImagePreview({
	previewUri,
	onClick,
	label,
	icon,
	className,
	objectPosition = "object-cover",
	error = false,
	isRequired,
	onRemove,
	uploadStatus,
	uploadErrorMessage,
	onRetry,
}: CameraPreviewProps) {
	const [expandImage, setExpandImage] = useState(false);
	const isUploading = uploadStatus === FileUploadStatus.Uploading;
	const isUploadError = uploadStatus === FileUploadStatus.Error;

	if (previewUri) {
		return (
			<div className={cx("relative h-24 w-full", className)}>
				{!!expandImage && (
					<ExpandedImage onClose={setExpandImage} previewUri={previewUri} />
				)}
				<button
					className="h-full w-full"
					disabled={isUploading}
					onClick={() => setExpandImage(true)}
					type="button"
				>
					<img
						alt="Imagem de preview"
						className={cx("mx-auto h-full w-full", objectPosition)}
						height={96}
						src={previewUri}
						width={100}
					/>
				</button>

				{!!isUploading && (
					<div
						aria-live="polite"
						className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/50"
					>
						<Spinner className="size-8" />
					</div>
				)}

				{!!isUploadError && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-sm bg-black/60 p-2">
						<p className="px-1 text-center text-white text-xs leading-snug">
							{uploadErrorMessage ?? "Falha no envio. Tente novamente."}
						</p>
						<Button
							onClick={(event) => {
								event.stopPropagation();
								onRetry?.();
							}}
							size="sm"
							type="button"
							variant="secondary"
						>
							Tentar novamente
						</Button>
					</div>
				)}

				{!isUploading && (
					<Edit05
						className="absolute top-2 right-2 size-9 rounded-lg bg-brand-solid p-2 text-button-primary-icon active:opacity-80"
						onClick={onClick}
					/>
				)}

				{!isRequired && onRemove && !isUploading && (
					<button onClick={onRemove} type="button">
						<Trash01 className="absolute top-2 left-2 size-9 rounded-lg bg-red-600 p-2 text-white active:opacity-80" />
					</button>
				)}
			</div>
		);
	}

	return (
		<Button
			className={cx(
				"flex h-24 w-full flex-col gap-1 rounded-sm border-2 p-2!",
				"border-primary border-dashed bg-secondary hover:bg-secondary",
				"active:bg-secondary active:shadow-none",
				!!error && "border-error",
				className
			)}
			onClick={onClick}
			type="button"
		>
			{icon === "camera" && (
				<Camera01 className="size-7 text-utility-gray-500" />
			)}
			{icon === "edit" && <Edit05 className="size-7 text-utility-gray-500" />}
			<span className="text-quaternary text-sm">{label}</span>
		</Button>
	);
}
